import {useCallback, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Skeleton,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useProducts} from '@amblique/king-living/app/hooks/use-products';

import {filterVisibleOptionProducts} from '@amblique/king-living/app/utils/customize-selections';
import {GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';

import OptionalAddOnHeader from '@amblique/king-living/app/components/product-view/optional-add-ons/optional-add-on-header';
import OptionalAddOnSingle from '@amblique/king-living/app/components/product-view/optional-add-ons/optional-add-on-single';
import OptionalAddOnStacked from '@amblique/king-living/app/components/product-view/optional-add-ons/optional-add-on-stacked';
import StandaloneViewDetailsModal from '@amblique/king-living/app/components/standalone-view-details-modal';

/**
 * Returns the primary image URL for a product object.
 * Tries c_pimlyConfigurationImage first (array), then imageGroups.
 *
 * @param {Object} product
 * @returns {string|null}
 */
function getProductImageUrl(product) {
  if (!product) return null;
  if (Array.isArray(product.c_pimlyConfigurationImage) && product.c_pimlyConfigurationImage[0]) {
    return product.c_pimlyConfigurationImage[0];
  }

  return product.imageGroups?.[0]?.images?.[0]?.link ?? null;
}

/**
 * Optional add-on group for one slot.
 * Fetches slot products, renders the shared header, decides Single (1 product) vs Stacked (2+ products) layout,
 * manages the "See details" view. Optional add-ons are never auto-preselected (isRequired: false).
 *
 * Header rendering (uses first product's data):
 * - Title: Recommendation type name (value.typeName, e.g., "Base", "Ottoman", "Chaise")
 * - Description: First product's shortDescription
 * - Image: First product's image (single layout only)
 *
 * Layout variants:
 * - Single layout (1 product): Shows image + title + description in header. No "See details" button (info already visible).
 * - Stacked layout (2+ products): Shows title + description only (no image in header, images in rows). Has "See details" button.
 *
 * View Details behavior (stacked layout only):
 * - Desktop: Shows ViewDetails in main section (product-view__below-gallery-section) via onAddOnDetailsOpen
 * - Mobile: Opens StandaloneViewDetailsModal (fullscreen drawer)
 *
 * @param {Object} props
 * @param {number} props.slotIndex - Slot index within the optionalAddOns array
 * @param {Object} props.value - Enriched slot value from getOptionalAddOns()
 * @param {string|null} props.selectedProductId - Currently selected product ID for this slot
 * @param {boolean} [props.isDisabled] - Whether the section is locked
 * @param {Function} props.onSelectionChange - Called with (slotIndex, product|null)
 * @param {Function} [props.onAddOnDetailsOpen] - Handler for opening add-on details (desktop only, stacked layout only)
 */
const OptionalAddOnGroup = ({
  slotIndex,
  value,
  selectedProductId,
  isDisabled = false,
  onSelectionChange,
  onAddOnDetailsOpen,
}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('OptionalAddOns', {});

  const isMobile = useBreakpointValue({base: true, lg: false}) === true;
  const [mobileDrawerProductId, setMobileDrawerProductId] = useState(null);

  // Memoized to keep the useProducts query key stable across unrelated re-renders.
  const visibleProductEntries = useMemo(
    () => filterVisibleOptionProducts(value?.products),
    [value?.products]
  );
  const visibleProductIds = useMemo(
    () => visibleProductEntries.map((p) => p.productId),
    [visibleProductEntries]
  );
  const hasProducts = visibleProductIds.length > 0;

  const {data: productsData, isLoading, isError} = useProducts(
    {
      parameters: {
        ...GET_PRODUCTS_PARAMS,
        ids: hasProducts ? visibleProductIds.join(',') : '',
      },
    },
    {enabled: hasProducts, staleTime: 5 * 60 * 1000}
  );

  const products = productsData?.data || [];

  const firstProduct = products[0] ?? null;
  const headerImageUrl = useMemo(() => getProductImageUrl(firstProduct), [firstProduct]);
  const headerDescription = firstProduct?.shortDescription ?? null;

  // Build recommendation name from typeName (same pattern as customize-selections.js)
  const groupTitle = value?.typeName ?? null;

  // Memoize derived render data to keep child prop references stable across re-renders.
  const stackedProducts = useMemo(
    () => products.map((p) => ({
      productId: p.id,
      name: p.name ?? '',
      price: p.price ?? null,
      imageUrl: getProductImageUrl(p),
    })),
    [products]
  );

  const handleSingleChange = useCallback((productId, checked) => {
    if (checked) {
      const product = products.find((p) => p.id === productId);

      onSelectionChange(slotIndex, product ?? null);
    } else {
      onSelectionChange(slotIndex, null);
    }
  }, [onSelectionChange, products, slotIndex]);

  const handleStackedChange = useCallback((productId) => {
    if (productId == null) {
      onSelectionChange(slotIndex, null);
    } else {
      const product = products.find((p) => p.id === productId);

      onSelectionChange(slotIndex, product ?? null);
    }
  }, [onSelectionChange, products, slotIndex]);

  const handleDetailsClick = useCallback((productId) => {
    const product = products.find((p) => p.id === productId);

    if (!product) return;

    if (isMobile) {
      setMobileDrawerProductId(productId);
    } else {
      onAddOnDetailsOpen?.(product);
    }
  }, [products, isMobile, onAddOnDetailsOpen]);

  const handleMobileDrawerClose = useCallback(() => {
    setMobileDrawerProductId(null);
  }, []);

  const mobileDrawerProduct = useMemo(() => {
    return products.find((p) => p.id === mobileDrawerProductId) ?? null;
  }, [products, mobileDrawerProductId]);

  if (!hasProducts) return null;

  // Safety check: if fetch completed but returned no products, hide the group
  if (!isLoading && !isError && products.length === 0) {
    return null;
  }

  return (
    <Box className="optional-add-on-group__root" {...styles.group}>
      {isError ? (
        <Box opacity={0.6} paddingY={4} textAlign="center">
          <Text color="gray.600" fontSize="sm">
            {intl.formatMessage({
              defaultMessage: 'Unable to load add-on products. Please refresh the page.',
              id: 'optional_add_ons.error.unable_to_load',
            })}
          </Text>
        </Box>
      ) : isLoading ? (
        <Box>
          <Skeleton height="60px" marginBottom={3} />
          <Skeleton height="40px" marginBottom={2} />
          {visibleProductIds.length > 1 && <Skeleton height="40px" />}
        </Box>
      ) : (
        <>
          <OptionalAddOnHeader
            description={headerDescription}
            groupTitle={groupTitle}
            imageAlt={groupTitle || firstProduct?.name}
            imageUrl={products.length === 1 ? headerImageUrl : null}
          />

          {products.length === 1 ? (
            <OptionalAddOnSingle
              isChecked={selectedProductId === products[0].id}
              isDisabled={isDisabled}
              name={products[0].name ?? ''}
              price={products[0].price ?? null}
              productId={products[0].id}
              onChange={handleSingleChange}
            />
          ) : (
            <OptionalAddOnStacked
              isDisabled={isDisabled}
              products={stackedProducts}
              selectedProductId={selectedProductId}
              onChange={handleStackedChange}
              onDetailsClick={handleDetailsClick}
            />
          )}
        </>
      )}

      {isMobile && (
        <StandaloneViewDetailsModal
          isOpen={Boolean(mobileDrawerProductId)}
          product={mobileDrawerProduct}
          onClose={handleMobileDrawerClose}
        />
      )}
    </Box>
  );
};

OptionalAddOnGroup.displayName = 'OptionalAddOnGroup';

OptionalAddOnGroup.propTypes = {
  slotIndex: PropTypes.number.isRequired,
  value: PropTypes.shape({
    recommendationTypeId: PropTypes.number,
    typeName: PropTypes.string,
    recommendationName: PropTypes.string,
    isRequired: PropTypes.bool,
    isVisible: PropTypes.bool,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        productId: PropTypes.string.isRequired,
        isHidden: PropTypes.bool,
        isPreselected: PropTypes.bool,
      })
    ),
  }).isRequired,
  selectedProductId: PropTypes.string,
  isDisabled: PropTypes.bool,
  onSelectionChange: PropTypes.func.isRequired,
  onAddOnDetailsOpen: PropTypes.func,
};

export default OptionalAddOnGroup;
